import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import type React from 'react';
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useAccount } from '../../contexts/AccountContext';
import { firestore } from '../../services/firebase';
import { generateQuickAnalysis } from '../../services/procurementService';
import type {
  Evaluation,
  EvaluatorAssignment,
  QuickAnalysis,
  Rubric,
  RubricCriteria,
  Supplier,
} from '../../types/procurement';
import { type Column, StandardTable } from '../common/UI';

interface Props {
  procurementId: string;
  evaluations: Evaluation[];
  rubric: Rubric;
  suppliers: Supplier[];
  evaluatorAssignments: EvaluatorAssignment[];
  quickAnalyses?: QuickAnalysis[];
  onShowSnackbar?: (message: string, severity: 'success' | 'error') => void;
}

const getScoreShading = (
  score: string | number | boolean
): {
  themeColor: 'success' | 'warning' | 'error' | 'transparent';
  alphaLevel: number;
  text: string;
} => {
  if (score === '-' || score === '') {
    return { themeColor: 'transparent', alphaLevel: 0, text: 'text.primary' };
  }

  const s = String(score).toLowerCase();

  // Boolean/Pass-Fail
  if (s === 'true' || s === 'pass') {
    return { themeColor: 'success', alphaLevel: 0.1, text: 'success.dark' };
  }
  if (s === 'false' || s === 'fail') {
    return { themeColor: 'error', alphaLevel: 0.1, text: 'error.dark' };
  }

  // Fuzzy Linguistic
  if (s === 'highly_confident') {
    return { themeColor: 'success', alphaLevel: 0.2, text: 'success.dark' };
  }
  if (s === 'confident') {
    return { themeColor: 'success', alphaLevel: 0.1, text: 'success.dark' };
  }
  if (s === 'minor_reservations') {
    return { themeColor: 'warning', alphaLevel: 0.15, text: 'warning.dark' };
  }
  if (s === 'major_reservations') {
    return { themeColor: 'error', alphaLevel: 0.2, text: 'error.main' };
  }
  if (s === 'unacceptable') {
    return { themeColor: 'error', alphaLevel: 0.3, text: 'error.dark' };
  }

  // Numeric (0-10)
  const num = Number(score);
  if (!Number.isNaN(num)) {
    if (num <= 2) {
      return { themeColor: 'error', alphaLevel: 0.3, text: 'error.dark' };
    }
    if (num <= 4) {
      return { themeColor: 'error', alphaLevel: 0.1, text: 'error.main' };
    }
    if (num <= 6) {
      return { themeColor: 'warning', alphaLevel: 0.15, text: 'warning.dark' };
    }
    if (num <= 8) {
      return { themeColor: 'success', alphaLevel: 0.1, text: 'success.main' };
    }
    return { themeColor: 'success', alphaLevel: 0.2, text: 'success.dark' };
  }

  return { themeColor: 'transparent', alphaLevel: 0, text: 'text.primary' };
};

const ModerationConsole: React.FC<Props> = ({
  procurementId,
  evaluations,
  rubric,
  suppliers,
  evaluatorAssignments,
  quickAnalyses = [],
  onShowSnackbar,
}) => {
  const { account } = useAccount();
  const [selectedAnalysisId, setSelectedAnalysisId] = useState<string | null>(
    (quickAnalyses || [])[0]?.id || null
  );
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Keep selectedAnalysisId synced if a new one is added
  useEffect(() => {
    if ((quickAnalyses || []).length > 0 && !selectedAnalysisId) {
      setSelectedAnalysisId((quickAnalyses || [])[0]?.id || null);
    }
  }, [quickAnalyses, selectedAnalysisId]);

  const handleQuickAnalysis = async () => {
    setIsAnalyzing(true);
    try {
      const payloadData = {
        suppliers: suppliers.map((s) => ({
          id: s.id,
          name: s.name,
          documents: s.documents,
          status: s.status,
        })),
        rubric: rubric.criteria,
        evaluations: evaluations,
        evaluatorAssignments: evaluatorAssignments,
      };
      const result = await generateQuickAnalysis(
        procurementId,
        account?.clientId || '',
        account?.uid || '',
        payloadData
      );
      if (result.success && result.analysis_text) {
        await addDoc(collection(firestore, 'procurements', procurementId, 'quickAnalyses'), {
          procurementId,
          analysisText: result.analysis_text,
          createdAt: serverTimestamp(),
          createdBy: account?.uid || 'unknown',
        });
        onShowSnackbar?.('Quick analysis generated and saved successfully.', 'success');
      } else {
        onShowSnackbar?.(`Failed to generate analysis: ${result.error}`, 'error');
      }
    } catch (e) {
      onShowSnackbar?.(`Error: ${(e as Error).message}`, 'error');
    }
    setIsAnalyzing(false);
  };

  const selectedAnalysis = quickAnalyses.find((qa) => qa.id === selectedAnalysisId);

  const suppliersWithEvals = suppliers.filter((s) =>
    evaluations.some((e) => e.supplierId === s.id)
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="h5" gutterBottom>
          Moderation Console
        </Typography>
        <Box component="ul" sx={{ mt: 0, pl: 2, color: 'text.secondary', '& li': { mb: 0.5 } }}>
          <Typography component="li" variant="body2">
            Review the evaluation matrix to compare scores from all assigned evaluators
            side-by-side.
          </Typography>
          <Typography component="li" variant="body2">
            Identify scoring anomalies and resolve disagreements within each supplier.
          </Typography>
          <Typography component="li" variant="body2">
            Guide panel discussions to reach a final, moderated consensus.
          </Typography>
        </Box>
      </Box>

      <Card variant="outlined" sx={{ bgcolor: 'background.default', mb: 2 }}>
        <CardHeader
          sx={{ pb: 1 }}
          title="AI Quick Analysis"
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }}
          action={
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleQuickAnalysis}
              disabled={isAnalyzing || evaluations.length === 0}
              startIcon={
                isAnalyzing ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />
              }
            >
              Generate Quick Analysis
            </Button>
          }
        />
        <Divider />
        <CardContent sx={{ p: 0 }}>
          {quickAnalyses.length > 0 ? (
            <Box sx={{ display: 'flex' }}>
              <Box
                sx={{
                  width: 250,
                  borderRight: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: 400,
                  overflowY: 'auto',
                }}
              >
                {quickAnalyses
                  .sort((a, b) => {
                    const timeA = a.createdAt ? new Date(a.createdAt).getTime() : Date.now();
                    const timeB = b.createdAt ? new Date(b.createdAt).getTime() : Date.now();
                    return timeB - timeA;
                  })
                  .map((qa) => (
                    <Box
                      key={qa.id}
                      onClick={() => setSelectedAnalysisId(qa.id)}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        borderBottom: 1,
                        borderColor: 'divider',
                        bgcolor: selectedAnalysisId === qa.id ? 'action.selected' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={selectedAnalysisId === qa.id ? 'bold' : 'normal'}
                      >
                        {qa.createdAt
                          ? new Date(
                              // @ts-expect-error Firebase timestamp handling
                              qa.createdAt.seconds ? qa.createdAt.seconds * 1000 : qa.createdAt
                            ).toLocaleString()
                          : 'Just now'}
                      </Typography>
                    </Box>
                  ))}
              </Box>
              <Box sx={{ flex: 1, p: 2, maxHeight: 400, overflowY: 'auto' }}>
                <Box
                  sx={{
                    '& h1, & h2, & h3': { mt: 0, mb: 1, fontWeight: 'bold', fontSize: '1.1rem' },
                    '& p': { mb: 1, fontSize: '0.9rem' },
                    '& ul': { mt: 0, mb: 1, pl: 2, fontSize: '0.9rem' },
                  }}
                >
                  <ReactMarkdown>{selectedAnalysis?.analysisText || ''}</ReactMarkdown>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                Click 'Generate Quick Analysis' to have the AI synthesize the current evaluation
                data into a brief summary of themes, variances, and leading submissions.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {evaluations.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No evaluations submitted yet.
        </Typography>
      ) : suppliersWithEvals.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No active suppliers have evaluations.
        </Typography>
      ) : (
        suppliersWithEvals.map((supplier) => {
          const supplierEvals = evaluations.filter((e) => e.supplierId === supplier.id);

          const columns: Column<RubricCriteria>[] = [
            {
              id: 'criteria',
              label: 'Criteria',
              render: (row) => `${row.category} - ${row.name}`,
            },
            ...supplierEvals.map((e): Column<RubricCriteria> => {
              let evaluatorName = `Eval ${e.evaluatorId.substring(0, 4)}`;
              const assignment = evaluatorAssignments.find((ea) => ea.userId === e.evaluatorId);
              if (assignment) {
                evaluatorName = assignment.userName;
              } else if (e.evaluatorId === account?.uid) {
                evaluatorName = account?.displayName || 'Me (Chair)';
              }

              return {
                id: `evaluator-${e.evaluatorId}`,
                label: evaluatorName,
                render: (row) => {
                  const scoreObj = e.scores.find(
                    (s) => s.criteriaId === row.id || s.criteriaId === row.name
                  );
                  const scoreVal = scoreObj ? scoreObj.score : '-';
                  return String(scoreVal).replace('_', ' ');
                },
                cellSx: (row) => {
                  const scoreObj = e.scores.find(
                    (s) => s.criteriaId === row.id || s.criteriaId === row.name
                  );
                  const scoreVal = scoreObj ? scoreObj.score : '-';

                  const { themeColor, alphaLevel, text } = getScoreShading(scoreVal);

                  return {
                    bgcolor: (theme) =>
                      themeColor === 'transparent'
                        ? 'transparent'
                        : alpha(theme.palette[themeColor].main, alphaLevel),
                    color: text,
                    fontWeight: scoreVal !== '-' ? 'bold' : 'normal',
                    textTransform: 'capitalize',
                    textAlign: 'center',
                    p: 1.5,
                  };
                },
              };
            }),
          ];

          return (
            <Card key={supplier.id} sx={{ mb: 2 }}>
              <CardHeader
                sx={{ bgcolor: 'action.hover' }}
                title={`Moderation Matrix: ${supplier.name}`}
                subheader="Mathematical averaging is disabled. The panel must actively discuss variances and agree on final moderated outcomes."
              />
              <Divider />
              <CardContent>
                <StandardTable
                  columns={columns}
                  data={rubric.criteria}
                  keyExtractor={(row) => row.id}
                  size="small"
                  emptyStateMessage="No criteria found."
                />
              </CardContent>
            </Card>
          );
        })
      )}
    </Box>
  );
};

export default ModerationConsole;
